import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getSystemLogs } from '../services/systemLogs';
import { SystemLog } from '../types/SystemLog';

// Define different polling intervals (in milliseconds)
const POLLING_INTERVALS = {
  HIGH_ACTIVITY: 2000,    // 2 seconds - when system is very active
  NORMAL: 30000,          // 30 seconds - normal operation
  LOW_ACTIVITY: 120000,   // 2 minutes - when system is idle
  PAUSED: 0,              // Stop polling when tab is not visible
} as const;

// Hook to detect page visibility (when user switches tabs)
const usePageVisibility = () => {
  const [isVisible, setIsVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
};

// Hook to determine system activity level based on log frequency
const useSystemActivity = (logs: SystemLog[] | undefined) => {
  const [activityLevel, setActivityLevel] = useState<'high' | 'normal' | 'low'>('normal');

  useEffect(() => {
    if (!logs || logs.length < 2) {
      setActivityLevel('normal');
      return;
    }

    // Check how many logs were created in the last 5 minutes
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const recentLogs = logs.filter(log => new Date(log.timestamp) > fiveMinutesAgo);

    if (recentLogs.length >= 10) {
      setActivityLevel('high');    // 10+ logs in 5 minutes = high activity
    } else if (recentLogs.length >= 3) {
      setActivityLevel('normal');  // 3-9 logs in 5 minutes = normal activity
    } else {
      setActivityLevel('low');     // Less than 3 logs = low activity
    }
  }, [logs]);

  return activityLevel;
};

// Main hook that combines everything
export const useSystemLogs = () => {
  const isPageVisible = usePageVisibility();
  const [currentInterval, setCurrentInterval] = useState<number>(POLLING_INTERVALS.NORMAL);
  
  // First, fetch logs with React Query
  const query = useQuery({
    queryKey: ['systemLogs'],
    queryFn: getSystemLogs,
    // Use the current interval state
    refetchInterval: currentInterval,
    // Enable/disable polling based on page visibility
    enabled: isPageVisible,
    // Refetch when the window becomes visible again
    refetchOnWindowFocus: isPageVisible,
  });

  const activityLevel = useSystemActivity(query.data);

  // Calculate the appropriate polling interval
  const getPollingInterval = (activityLevel: 'high' | 'normal' | 'low'): number => {
    if (!isPageVisible) return 0; // 0 = stop polling
    
    switch (activityLevel) {
      case 'high':
        return POLLING_INTERVALS.HIGH_ACTIVITY;
      case 'normal':
        return POLLING_INTERVALS.NORMAL;
      case 'low':
        return POLLING_INTERVALS.LOW_ACTIVITY;
      default:
        return POLLING_INTERVALS.NORMAL;
    }
  };

  // Update polling interval when activity level or page visibility changes
  useEffect(() => {
    const newInterval = getPollingInterval(activityLevel);
    if (newInterval !== currentInterval) {
      setCurrentInterval(newInterval);
    }
  }, [activityLevel, isPageVisible, currentInterval]);

  // Return the query result plus some additional info
  return {
    ...query,
    activityLevel,
    isPageVisible,
    pollingInterval: currentInterval,
    // Helper method to force refresh
    forceRefresh: () => query.refetch(),
  };
}; 